<!-- Показывает позиции счёта заказа и подтверждает удаление позиции -->
<template>
  <div class="items-list">
    <div class="info-section">
      <div class="card-header">
        <div class="header-left">
          <h4 class="card-title">Позиции счёта</h4>
          <span v-if="localItems.length" class="items-count">
            {{ localItems.length }}
          </span>
        </div>
        <div class="left-actions">
          <base-button
            v-if="!isCreating"
            @click="$router.push({ name: 'ProductsRegistry' })"
          >
            Реестр продуктов
          </base-button>
          <base-primary-button
            v-if="!isCreating"
            @click="startCreating"
          >
            Добавить позицию
          </base-primary-button>
        </div>
      </div>

      <div class="card-content">
        <base-loader v-if="isLocalLoading" />

        <div v-else>
          <OrderInvoiceItemForm
            v-if="isCreating"
            :order-id="orderId"
            :invoice-id="invoiceId"
            :company-id="companyId"
            :is-create-mode="true"
            @save="handleCreateSuccess"
            @cancel="isCreating = false"
          />

          <div
            v-if="!localItems.length && !isCreating"
            class="no-data"
          >
            Позиций нет
          </div>

          <div
            v-for="item in localItems"
            :key="item.id"
            class="item-card"
          >
            <div v-if="editingItemId === item.id" class="item-edit">
              <OrderInvoiceItemForm
                :order-id="orderId"
                :invoice-id="invoiceId"
                :company-id="companyId"
                :initial-data="item"
                @save="handleUpdateSuccess"
                @cancel="editingItemId = null"
              />
            </div>

            <div v-else class="item-view">
              <div class="item-main">
                <div class="item-name-group">
                  <span class="item-name">{{ item.name }}</span>
                  <span
                    class="item-description"
                    v-if="item.description"
                  >
                    {{ item.description }}
                  </span>
                </div>

                <div class="item-amounts">
                  <div class="amount-group">
                    <span class="amount-label">Кол-во</span>
                    <span class="amount-value">
                      {{ item.quantity }}
                      <span class="amount-unit" v-if="item.unit">
                        {{ item.unit }}
                      </span>
                    </span>
                  </div>
                  <span class="amount-sep">×</span>
                  <div class="amount-group">
                    <span class="amount-label">Цена</span>
                    <span class="amount-value">
                      {{ formatAmount(item.price) }}
                    </span>
                  </div>
                  <span class="amount-sep">=</span>
                  <div class="amount-group">
                    <span class="amount-label">Без НДС</span>
                    <span class="amount-value">
                      {{ formatAmount(item.base_amount) }}
                    </span>
                  </div>
                  <div class="amount-group" v-if="item.vat_amount">
                    <span class="amount-label">
                      {{
                        item.vat_kind === "zero"
                          ? "НДС 0%"
                          : item.vat_rate
                          ? `НДС ${item.vat_rate}%`
                          : "Без НДС"
                      }}
                    </span>
                    <span class="amount-value amount-vat">
                      {{ formatAmount(item.vat_amount) }}
                    </span>
                  </div>
                  <div
                    class="amount-group"
                    v-if="item.vat_calculation_type"
                  >
                    <span class="amount-label">Способ НДС</span>
                    <span class="amount-value">
                      {{
                        formatVatCalculationType(
                          item.vat_calculation_type
                        )
                      }}
                    </span>
                  </div>
                  <div class="amount-group">
                    <span class="amount-label">Итого</span>
                    <span class="amount-value amount-total">
                      {{ formatAmount(item.total_amount) }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="item-actions">
                <base-button @click="editingItemId = item.id">
                  Изменить
                </base-button>
                <base-button
                  class="btn-danger"
                  @click="removeItem(item.id)"
                >
                  Удалить
                </base-button>
              </div>
            </div>
          </div>

          <div
            class="items-footer"
            v-if="localItems.length || invoiceTotals"
          >
            <div class="footer-amounts">
              <div
                class="footer-row"
                v-if="invoiceTotals && invoiceTotals.base_amount"
              >
                <span class="footer-label">Сумма без НДС:</span>
                <span class="footer-value">
                  {{ formatAmount(invoiceTotals.base_amount) }}
                  {{ invoiceTotals.currency || "RUB" }}
                </span>
              </div>
              <div
                class="footer-row"
                v-if="invoiceTotals && invoiceTotals.vat_amount"
              >
                <span class="footer-label">Сумма НДС:</span>
                <span class="footer-value">
                  {{ formatAmount(invoiceTotals.vat_amount) }}
                  {{ invoiceTotals.currency || "RUB" }}
                </span>
              </div>
              <div
                class="footer-row footer-row--total"
                v-if="invoiceTotals && invoiceTotals.total_amount"
              >
                <span class="footer-label">Итого по счёту:</span>
                <span class="footer-total">
                  {{ formatAmount(invoiceTotals.total_amount) }}
                  {{ invoiceTotals.currency || "RUB" }}
                </span>
              </div>
              <div
                class="footer-words"
                v-if="
                  invoiceTotals && invoiceTotals.total_amount_in_words
                "
              >
                {{ invoiceTotals.total_amount_in_words }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <BaseDialog
      :visible="Boolean(itemIdPendingDelete)"
      title="Удалить позицию?"
      message="Позиция будет удалена из счёта"
      :actions="[
        { key: 'confirm', label: 'Удалить', type: 'danger' },
        { key: 'close', label: 'Отмена', type: 'secondary' },
      ]"
      @action="handleDeleteItemDialogAction"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";
import { useOrderInvoiceItemStore } from "@/stores/orderInvoiceItemStore";
import { useOrderInvoiceProductStore } from "@/stores/orderInvoiceProductStore";
import { useNotificationStore } from "@/stores/notificationStore";

import OrderInvoiceItemForm from "./OrderInvoiceItemForm.vue";

export default {
  name: "OrderInvoiceItemsList",
  components: { OrderInvoiceItemForm },
  props: {
    invoiceId: { type: [String, Number], required: true },
    orderId: { type: [String, Number], required: true },
    companyId: { type: [String, Number], required: true },
    invoiceTotals: { type: Object, default: null },
  },
  emits: ["items-updated"],
  data() {
    return {
      isLocalLoading: false,
      isCreating: false,
      editingItemId: null,
      itemIdPendingDelete: null,
    };
  },
  computed: {
    ...mapState(useOrderInvoiceItemStore, ["items"]),
    ...mapState(useOrderInvoiceProductStore, [
      "vatCalculationTypeOptions",
    ]),

    notificationStore() {
      return useNotificationStore();
    },

    localItems() {
      return this.items[this.invoiceId] || [];
    },
  },
  methods: {
    ...mapActions(useOrderInvoiceItemStore, [
      "getItemsRequest",
      "deleteItem",
    ]),

    async loadItems() {
      this.isLocalLoading = true;
      try {
        await this.getItemsRequest(this.orderId, this.invoiceId);
      } finally {
        this.isLocalLoading = false;
      }
    },

    startCreating() {
      this.editingItemId = null;
      this.isCreating = true;
    },

    async handleCreateSuccess() {
      this.isCreating = false;
      this.notificationStore.showSuccess("Позиция добавлена");
      await this.loadItems();
      this.$emit("items-updated");
    },

    async handleUpdateSuccess() {
      this.editingItemId = null;
      this.notificationStore.showSuccess("Позиция обновлена");
      await this.loadItems();
      this.$emit("items-updated");
    },

    removeItem(itemId) {
      this.itemIdPendingDelete = itemId;
    },

    async handleDeleteItemDialogAction(action) {
      if (action !== "confirm") {
        this.itemIdPendingDelete = null;
        return;
      }

      const itemId = this.itemIdPendingDelete;
      this.itemIdPendingDelete = null;
      if (!itemId) return;

      try {
        await this.deleteItem(this.orderId, this.invoiceId, itemId);
        this.notificationStore.showSuccess("Позиция удалена");
        await this.loadItems();
        this.$emit("items-updated");
      } catch (error) {
        console.error("Ошибка удаления позиции:", error);
      }
    },

    formatVatCalculationType(value) {
      const option = this.vatCalculationTypeOptions.find(
        (o) => o.value === value
      );
      return option ? option.label : value;
    },

    formatAmount(value) {
      return parseFloat(value || 0).toLocaleString("ru-RU", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
  async created() {
    await this.loadItems();
  },
};
</script>

<style scoped>
.info-section {
  border-radius: 10px;
  border: 1px solid #e8e8e8;
  overflow: hidden;
  background: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--base-in-indent);
  background: rgb(240, 240, 240);
  border-bottom: 1px solid #e8e8e8;
  gap: 15px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-dark);
  margin: 0;
}

.items-count {
  background: #667eea;
  color: #fff;
  border-radius: 12px;
  padding: 3px 10px;
  min-width: 20px;
  font-weight: 600;
  text-align: center;
  font-size: 13px;
}

.left-actions {
  display: flex;
  gap: 10px;
}

.card-content {
  padding: var(--base-in-indent);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.item-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.item-view {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px var(--base-in-indent);
  gap: 15px;
}

.item-edit {
  padding: 0;
}

.item-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.item-name-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.item-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-dark);
}

.item-description {
  font-size: 12px;
  color: #888;
}

.item-amounts {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.amount-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.amount-label {
  font-size: 11px;
  color: #999;
}

.amount-value {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-dark);
}

.amount-unit {
  font-size: 12px;
  color: #888;
  margin-left: 2px;
}

.amount-vat {
  color: var(--color-yellow);
}

.amount-total {
  font-weight: 700;
  color: #4f46e5;
}

.amount-sep {
  font-size: 16px;
  color: #ccc;
  align-self: flex-end;
  padding-bottom: 2px;
}

.item-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.btn-danger {
  background-color: #fff;
  border: 1px solid var(--color-danger) !important;
  color: var(--color-danger) !important;
}

.btn-danger:hover {
  background-color: var(--color-danger) !important;
  color: #fff !important;
}

.items-footer {
  border-top: 1px solid #e8e8e8;
  padding: 14px var(--base-in-indent);
  background: #f9f9f9;
  border-radius: 0 0 8px 8px;
}

.footer-amounts {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.footer-row {
  display: flex;
  gap: 16px;
  align-items: baseline;
}

.footer-row--total {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px dashed #e0e0e0;
}

.footer-label {
  font-size: 14px;
  color: #666;
}

.footer-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-dark);
  min-width: 140px;
  text-align: right;
}

.footer-total {
  font-size: 16px;
  font-weight: 700;
  color: #4f46e5;
  min-width: 140px;
  text-align: right;
}

.footer-words {
  font-size: 12px;
  font-style: italic;
  color: #888;
  margin-top: 2px;
}

@media (max-width: 768px) {
  .item-view {
    flex-direction: column;
    align-items: flex-start;
  }

  .item-actions {
    width: 100%;
  }

  .item-amounts {
    gap: 8px;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .footer-amounts {
    align-items: flex-start;
  }

  .footer-value,
  .footer-total {
    min-width: auto;
    text-align: left;
  }
}
</style>
